import { LoadingState } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { Fragment, memo, useEffect, useState } from "react";
import AmendmentDetailsTab from "v2/components/LeaseAgreements/AmendmentDetailsTab";
import DetailsTab from "v2/components/LeaseAgreements/DetailsTab";

interface LeaseSummaryProps {
	/** `ProjectSerializer#lease_agreement_id` — the YARDI lease, not TCP's `Lease`. */
	leaseAgreementId?: string;
	/**
	 * `ProjectSerializer#amendment_id` — the uuid FK (`projects.amendment_id`),
	 * NOT the human-readable `yardi_amendment_id` ("10728826"). The two are
	 * different families of id; only the uuid addresses `GET /v2/amendments/:id`.
	 */
	amendmentId?: string;
}

/**
 * Read-only "Lease" step of the finalize wizard (`/projects/:id/schedule`),
 * between Summary and Scheduling.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT THE LEASE SECTION THAT CAME OUT ON 28 AUG
 * ---------------------------------------------------------------------------
 * The one removed then rendered TCP's own `Lease`, which the amendment ERD left
 * with four fields — every date and rent figure read "Unknown" / "$0.00" on a
 * Yardi project. This one reads the records that actually own those terms: the
 * `LeaseAgreement` and its `Amendment`. Nothing about that decision is reversed;
 * TCP's `Lease` is still not shown anywhere.
 *
 * IT FETCHES ITS OWN RECORDS, from the project's flat `lease_agreement_id` /
 * `amendment_id` attributes — the same pattern as Summary and the project
 * workspace tabs, and for the same reason: the project's INCLUDED relationships
 * come back empty on an amendment project, so an id is the only thing that can
 * be relied on. See Summary's docblock for the full account.
 *
 * IT REUSES THE LEASE'S OWN TABS rather than re-listing the fields here, so the
 * wizard and `/lease-agreements/:id` can never drift. Both are passed `hideEdit`
 * — this step states the record and offers no way to change it (Sim: "just show
 * like the summary page, no edit field"). `.summary-step.read-only` would kill
 * the click regardless, which is exactly why the button is removed rather than
 * left to look live and do nothing.
 *
 * A project with no lease agreement or no amendment renders that section's empty
 * state rather than disappearing — a section that silently vanishes reads as
 * broken. Same rule as Summary's Tenancy section.
 */
const useRecord = (path: string, modelName: string, id?: string) => {
	// undefined = nothing to fetch; null = in flight; false = refused or gone.
	const [record, setRecord] = useState<any | null | false | undefined>(id ? null : undefined);
	const [datasource] = useState(new Datasource({ mainModelName: modelName }));

	useEffect(() => {
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setRecord(response.success ? response.normalizedMainModelResponse || false : false)
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (_.isEmpty(id)) {
			setRecord(undefined);
			return;
		}
		setRecord(null);
		datasource.get("v2", `${path}/${id}`, {});
	}, [datasource, path, id]);

	return record;
};

const EmptyState = ({ message }: { message: string }) => (
	<div className="alert alert-light empty-state">{message}</div>
);

const LeaseSummary = ({ leaseAgreementId, amendmentId }: LeaseSummaryProps) => {
	const agreement = useRecord("lease-agreements", "lease_agreement", leaseAgreementId);
	const amendment = useRecord("amendments", "amendment", amendmentId);

	return (
		<div className="summary-step read-only">
			<Fragment>
				<h4>{translate("lease")}</h4>
				<div className="card">
					{agreement === null ? (
						<LoadingState />
					) : !!agreement ? (
						<DetailsTab agreement={agreement} hideEdit />
					) : (
						<EmptyState message={translate("lease_agreement_not_found") as string} />
					)}
				</div>
			</Fragment>
			<Fragment>
				<h4>{translate("amendment")}</h4>
				<div className="card">
					{amendment === null ? (
						<LoadingState />
					) : !!amendment ? (
						<AmendmentDetailsTab
							amendment={amendment}
							leaseAgreementId={leaseAgreementId || ""}
							hideEdit
						/>
					) : (
						<EmptyState message={translate("amendment_not_found") as string} />
					)}
				</div>
			</Fragment>
		</div>
	);
};

export default memo(LeaseSummary);
